/* eslint-disable max-lines */
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { PolylineSymbol, PolylineSymbolConstructorParams } from "@evergis/sgis/es/symbols/PolylineSymbol";
import { Polygon } from "@evergis/sgis/es/features/Polygon";
import { Crs } from "@evergis/sgis/es/Crs";
import { Render } from "@evergis/sgis/es/renders/Render";
import { Poly } from "@evergis/sgis/es/features/Poly";
import { FillStyle, PolyRender } from "@evergis/sgis/es/renders/Poly";
import { Coordinates } from "@evergis/sgis/es/baseTypes";
import { ArcPathRender } from "@evergis/sgis/es/renders/ArcPath";
import { createPointRender, createShadowRender } from "./getEditorPointRenderer";
import { getSimplifiedPolygonCoordinates } from "./getSimplifiedPolygonCoordinates";
import { ShadowedPointSymbol } from "./ShadowedPointSymbol";

export type EditorPolySymbolParams = {
  shadowColor: string;
  shadowSize: number;
  outerShadowColor?: string;
  outerShadowSize?: number;
  fillColor?: string;
  enclose?: boolean;
  showVertex?: boolean;
  vertexShadowSize?: number;
  vertexSize?: number;
  showOnlyVertex?: boolean;
} & PolylineSymbolConstructorParams;

const DEFAULT_VERTEX_SIZE = 6;
const DEFAULT_VERTEX_SHADOW_SIZE = 2;
const MAX_CACHED_POINTS_SIZE = 50000;
const PX_TOLERANCE = 2;

export class ShadowedPolySymbol extends PolylineSymbol {
  shadowColor!: string;

  shadowSize!: number;

  outerShadowColor!: string;

  outerShadowSize!: number;

  fillColor?: string;

  enclose?: boolean;

  showVertex?: boolean;

  vertexShadowSize?: number = DEFAULT_VERTEX_SHADOW_SIZE;

  vertexSize?: number = DEFAULT_VERTEX_SIZE;

  showOnlyVertex?: boolean;

  protected readonly options: EditorPolySymbolParams;

  private cachedResolution: number;

  private cachedWidth: number;

  private cachedHeight: number;

  private cachedExtent: string;

  private cachedCrs: Crs;

  private pointsRender: ArcPathRender = new ArcPathRender({ renders: [], path: new Path2D(), chunkSize: 50 });

  private pointsShadowsRender: ArcPathRender = new ArcPathRender({ renders: [], path: new Path2D(), chunkSize: 50 });

  private renderedPointsSet: Set<string> = new Set<string>();

  private maxCachedPoints: number = MAX_CACHED_POINTS_SIZE;

  constructor(options: EditorPolySymbolParams) {
    super(options);

    this.options = options;
  }

  clone() {
    return new ShadowedPolySymbol(this.options);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  public renderFunction(feature: Polygon, resolution: number, crs: Crs, painter: DomPainter): Render[] {
    if (!(feature instanceof Poly)) return [];

    if (
      this.cachedResolution !== resolution ||
      !this.cachedCrs?.equals(crs) ||
      this.cachedWidth !== painter.width ||
      this.cachedHeight !== painter.height ||
      this.cachedExtent !== painter.bbox.coordinates.toString()
    ) {
      this.renderedPointsSet = new Set<string>();
    }

    const coordinates = super.getRenderedCoordinates(feature, resolution, crs, PX_TOLERANCE);
    const simplifiedFeatureCoordinates = getSimplifiedPolygonCoordinates(feature, crs, resolution * PX_TOLERANCE);
    const polyRender = this.getPolyRender(coordinates, feature, simplifiedFeatureCoordinates);
    const shadowRender = this.getShadow(coordinates, feature, simplifiedFeatureCoordinates);
    const outerShadowRender = this.getOuterShadow(coordinates, feature, simplifiedFeatureCoordinates);
    const vertexRender = this.getVertexRender(coordinates, feature, simplifiedFeatureCoordinates, painter);
    let renders: Render[] = [polyRender];

    if (shadowRender && !this.showOnlyVertex) {
      renders = [shadowRender, polyRender];
    }

    if (outerShadowRender && shadowRender && !this.showOnlyVertex) {
      renders = [outerShadowRender, shadowRender, polyRender];
    }

    if (vertexRender) {
      renders = [...vertexRender, ...renders];
    }

    this.cachedResolution = resolution;
    this.cachedCrs = crs;
    this.cachedWidth = painter.width;
    this.cachedHeight = painter.height;
    this.cachedExtent = painter.bbox.coordinates.toString();

    return renders;
  }

  protected getOuterShadow(
    coordinates: Coordinates[][],
    feature: Polygon,
    mapCoordinates: Coordinates[][],
  ): PolyRender | void {
    if (!this.outerShadowColor || !this.outerShadowSize) {
      return;
    }

    return new PolyRender(coordinates, {
      fillStyle: FillStyle.None,
      enclosed: false,
      strokeColor: this.outerShadowColor,
      strokeWidth: this.outerShadowSize,
      lineDash: this.lineDash,
      mapCoordinates,
    });
  }

  protected getShadow(
    coordinates: Coordinates[][],
    feature: Polygon,
    mapCoordinates: Coordinates[][],
  ): PolyRender | void {
    return new PolyRender(coordinates, {
      fillStyle: FillStyle.None,
      enclosed: this.enclose,
      strokeColor: this.shadowColor,
      strokeWidth: this.shadowSize,
      lineDash: this.lineDash,
      mapCoordinates,
    });
  }

  protected getPolyRender(coordinates: Coordinates[][], feature: Polygon, mapCoordinates: Coordinates[][]): PolyRender {
    return new PolyRender(coordinates, {
      fillStyle: this.enclose ? FillStyle.Color : FillStyle.None,
      enclosed: this.enclose,
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      lineDash: this.lineDash,
      mapCoordinates,
    });
  }

  // eslint-disable-next-line max-lines-per-function
  protected getVertexRender(
    coordinates: Coordinates[][],
    feature: Polygon,
    mapCoordinates: Coordinates[][],
    painter: DomPainter,
  ): ArcPathRender[] | void {
    if (!this.showVertex) return;

    const pointRenderers = [];
    const shadowRenderers = [];
    const newRenderedPointsSet = new Set<string>();

    for (let i = 0; i < coordinates.length; i++) {
      const ring = coordinates[i];

      for (let j = 0; j < ring.length; j++) {
        const point = ring[j];
        const featurePoint = mapCoordinates[i][j];

        const pointKey = point.toString();

        const shadowRender = createShadowRender(point, {
          size: this.vertexSize,
          strokeWidth: 2,
          shadowColor: this.shadowColor,
          strokeColor: this.strokeColor,
          shadowSize: this.vertexShadowSize,
          fillColor: "#ffffff",
          mapCoordinates: featurePoint,
        });
        const pointRender = createPointRender(point, {
          size: this.vertexSize,
          strokeWidth: 2,
          shadowColor: this.shadowColor,
          strokeColor: this.strokeColor,
          shadowSize: this.vertexShadowSize,
          fillColor: "#ffffff",
          mapCoordinates: featurePoint,
        });

        if (this.renderedPointsSet.has(pointKey)) {
          shadowRender.updateNeeded = false;
          pointRender.updateNeeded = false;
        } else {
          this.renderedPointsSet.add(pointKey);
        }

        if (this.renderedPointsSet.size > this.maxCachedPoints) {
          newRenderedPointsSet.add(pointKey);
        }

        shadowRenderers.push(shadowRender);
        pointRenderers.push(pointRender);
      }
    }

    if (this.renderedPointsSet.size > this.maxCachedPoints) {
      this.renderedPointsSet = newRenderedPointsSet;
      this.maxCachedPoints *= 2;
    }

    this.pointsRender.updateRenders(pointRenderers, painter);
    this.pointsShadowsRender.updateRenders(shadowRenderers, painter);

    return [this.pointsShadowsRender, this.pointsRender];
  }
}

export const polySymbolParams = {
  strokeWidth: 2,
  strokeColor: "#e33600",
  shadowSize: 8,
  shadowColor: "rgba(227, 54, 0, 0.3)",
};

const measureSymbolParams = {
  strokeColor: "#ff6933",
  shadowSize: 0,
  vertexShadowSize: 0,
};

export const DEFAULT_EDITOR_POLYLINE_OPTIONS = {
  ...polySymbolParams,
  enclose: false,
  showVertex: true,
};

export const DEFAULT_EDITOR_POLYGON_OPTIONS = {
  ...polySymbolParams,
  fillColor: "rgba(255, 255, 255, 0.56)",
  enclose: true,
  showVertex: true,
};

export const polylineEditSymbol = new ShadowedPolySymbol(DEFAULT_EDITOR_POLYLINE_OPTIONS);

export const measureLengthSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  ...measureSymbolParams,
  enclose: false,
  showVertex: true,
});

export const measureAreaSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  ...measureSymbolParams,
  fillColor: "rgba(255, 105, 51, 0.25)",
  enclose: true,
  showVertex: true,
});

export const polylineTransformSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  enclose: false,
  showVertex: false,
});

export const polygonEditSymbol = new ShadowedPolySymbol(DEFAULT_EDITOR_POLYGON_OPTIONS);

export const polygonCreateSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  fillColor: "rgba(255, 255, 255, 0.56)",
  enclose: true,
  showVertex: false,
});

export const polylineCreateSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  enclose: false,
  showVertex: false,
});

export const polygonPointCreateSymbol = new ShadowedPolySymbol({
  vertexSize: 14,
  vertexShadowSize: 3,
  strokeWidth: 2,
  strokeColor: "#e33600",
  fillColor: "rgba(255, 255, 255, 0.46)",
  shadowColor: "rgba(227, 54, 0, 0.3)",
  shadowSize: 3,
  enclose: true,
  showVertex: true,
});

export const polygonTransformSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  fillColor: "rgba(255, 255, 255, 0.56)",
  enclose: true,
  showVertex: false,
});

export const multiPointTransformSymbol = new ShadowedPolySymbol({
  ...polySymbolParams,
  enclose: true,
  showVertex: true,
  fillColor: "transparent",
  strokeWidth: 0,
  showOnlyVertex: true,
});

const snapSymbolParams = {
  size: 10,
  strokeWidth: 2,
  strokeColor: "#e33600",
  fillColor: "#ffffff",
  shadowColor: "rgba(227, 54, 0, 0.3)",
  shadowSize: 3,
};

export const editorPolygonSnapSymbol = new ShadowedPointSymbol(snapSymbolParams);

export const snappingSymbolParams = {
  ...snapSymbolParams,
  size: 8,
  fillColor: "transparent",
  outlineRadius: 10,
  outlineStrokeWidth: 1,
  outlineDash: [2, 1],
};

export const snappingSymbol = new ShadowedPointSymbol(snappingSymbolParams);

export const measurePolygonSnapSymbol = new ShadowedPointSymbol({
  ...snapSymbolParams,
  strokeColor: "#ff6933",
  shadowSize: 0,
});
